import { createContext, use, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuthStore } from '../auth/authStore.js';
import { refreshSession } from '../lib/api/index.js';
import { createSocket, type OrbitSocket } from '../lib/socket.js';

export type SocketStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

interface SocketContextValue {
  socket: OrbitSocket | null;
  status: SocketStatus;
  /** Present when the last connection attempt failed. */
  error: string | null;
}

const DISCONNECTED: SocketContextValue = { socket: null, status: 'disconnected', error: null };

const SocketContext = createContext<SocketContextValue>(DISCONNECTED);

/**
 * Owns the single Socket.IO connection for the session.
 *
 * Split in two on purpose. The outer component decides *whether* there should be
 * a connection; the inner one owns it and is mounted only while signed in. That
 * makes the lifecycle a mount/unmount rather than an effect that resets state -
 * signing out unmounts the inner component, its cleanup closes the socket, and
 * the next sign-in mounts a fresh one that handshakes with the new token instead
 * of reusing a stale connection.
 *
 * Context is the right tool here, unlike for auth state: the socket instance is
 * only ever consumed by components, never by module-level code.
 */
export function SocketProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.status === 'authenticated');

  if (!isAuthenticated) {
    return <SocketContext value={DISCONNECTED}>{children}</SocketContext>;
  }

  return <ConnectedSocketProvider>{children}</ConnectedSocketProvider>;
}

function ConnectedSocketProvider({ children }: { children: ReactNode }) {
  // Lazy initialiser: built once per mount, and with `autoConnect: false` no
  // network activity happens until the effect below connects it.
  const [socket] = useState<OrbitSocket>(() => createSocket());
  const [status, setStatus] = useState<SocketStatus>('connecting');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let retry: ReturnType<typeof setTimeout> | undefined;

    socket.on('connect', () => {
      setStatus('connected');
      setError(null);
    });

    socket.on('connect_error', (cause) => {
      setStatus('error');
      // The server answers a rejected handshake with exactly this message.
      setError(cause.message === 'unauthorized' ? 'Session rejected' : 'Cannot reach the server');

      // A refused handshake does not retry by itself. Usually the access token
      // simply ran out - the laptop slept, say - so refresh the session and
      // connect again; if the session is over, the refresh has already signed
      // out with the server's reason.
      if (cause.message === 'unauthorized') {
        void refreshSession().then((outcome) => {
          if (outcome.status === 'ok') {
            socket.connect();
          } else if (outcome.status === 'failed') {
            clearTimeout(retry);
            retry = setTimeout(() => socket.connect(), 5_000);
          }
        });
      }
    });

    socket.on('disconnect', () => {
      setStatus('disconnected');
    });

    socket.connect();

    return () => {
      clearTimeout(retry);
      socket.removeAllListeners();
      socket.close();
    };
  }, [socket]);

  const value = useMemo<SocketContextValue>(
    () => ({ socket, status, error }),
    [socket, status, error],
  );

  return <SocketContext value={value}>{children}</SocketContext>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSocket(): SocketContextValue {
  return use(SocketContext);
}
